
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SubscriptionPlan } from '@/hooks/use-subscription';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';

interface PricingSectionProps {
  initialTab?: 'customers' | 'studios';
  onTabChange?: (tab: string) => void;
  onPlanSelect: (plan: string) => void;
  currentPlan?: SubscriptionPlan;
  showLoginButton?: boolean;
}

export function PricingSection({
  initialTab = 'customers',
  onTabChange,
  onPlanSelect,
  currentPlan,
  showLoginButton = false
}: PricingSectionProps) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (onTabChange) {
      onTabChange(value);
    }
  };

  const customerPlans = [
    {
      id: 'free',
      name: 'Basic',
      price: '0',
      period: '/monat',
      description: 'Perfekt für Gelegenheitsnutzer.',
      features: [
        'Zugang zu allen Studios',
        'Begrenzte Anzahl an Buchungen',
        'Einfache Buchungsverwaltung',
        'Email Support'
      ],
      popular: false
    },
    {
      id: 'pro',
      name: 'Premium',
      price: '1',
      period: '/monat',
      description: 'Für regelmäßige Nutzer mit erweiterten Funktionen.',
      features: [
        'Unbegrenzte Buchungen',
        'Favoriten speichern',
        'Kalendersynchronisierung',
        'Add-on Buchungen',
        'Priorisierter Support'
      ],
      popular: true
    }
  ];

  const studioPlans = [
    {
      id: 'studio_free',
      name: 'Studio Free',
      price: '0',
      period: '/monat',
      description: 'Für den Start in die Online-Buchung.',
      features: [
        '10 Buchungen/Monat',
        '1 Mitarbeiter',
        'Einfache Kalenderverwaltung',
        'Basis Support',
        'Buchungsverwaltung'
      ],
      popular: false,
      highlightColor: '#4C956C'
    },
    {
      id: 'studio_pro',
      name: 'Studio Pro',
      price: '19',
      period: '/monat',
      description: 'Für kleine Studios und Einzelunternehmer.',
      features: [
        '50 Buchungen/Monat',
        '3 Mitarbeiter',
        'Kalender-Synchronisation',
        'Statistik & Dashboard',
        'Referral Tracking',
        'Standard Support'
      ],
      popular: false,
      highlightColor: '#4C956C'
    },
    {
      id: 'studio_business',
      name: 'Business',
      price: '39',
      period: '/monat',
      description: 'Für wachsende Studios mit mehreren Mitarbeitern.',
      features: [
        '300 Buchungen/Monat',
        '10 Mitarbeiter',
        'Add-On Verwaltung',
        'Erweiterte Statistiken',
        'Bevorzugter Support',
        'Alle Pro-Features inklusive'
      ],
      popular: true,
      highlightColor: '#2C6E49'
    },
    {
      id: 'studio_premium',
      name: 'Premium',
      price: '59',
      period: '/monat',
      description: 'Umfassende Lösung für professionelle Studios.',
      features: [
        'Unbegrenzte Buchungen',
        'Unbegrenzte Mitarbeiter',
        'Direkter Support-Kontakt',
        'Vollständige Statistiken',
        'Alle Business-Features inklusive',
        'Individuelle Anpassungen'
      ],
      popular: false,
      highlightColor: '#1D4D33'
    }
  ];
  
  const renderPlanCard = (plan: any) => {
    const isCurrentPlan = plan.id === currentPlan;
    
    return (
      <motion.div
        key={plan.id}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <Card className={`h-full border ${plan.popular ? 'border-chrona-accent shadow-lg shadow-chrona-accent/10' : 'border-gray-200'} relative overflow-hidden`}>
          {plan.popular && (
            <div className="absolute top-0 right-0">
              <div className="bg-chrona-accent text-white text-xs px-3 py-1 font-medium transform rotate-0 translate-x-2 -translate-y-0 shadow-md">
                Empfohlen
              </div>
            </div>
          )}
          
          <CardHeader className="pb-0">
            <h3 className="text-xl font-bold">{plan.name}</h3>
            <div className="mt-2 mb-0">
              <span className="text-3xl font-bold">{plan.price}€</span>
              <span className="text-sm text-gray-500">{plan.period}</span>
            </div>
          </CardHeader>
          
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500 mb-4">{plan.description}</p>
            <ul className="space-y-2">
              {plan.features.map((feature: string, index: number) => (
                <li key={index} className="flex items-start">
                  <Check className="h-5 w-5 text-chrona-accent mr-2 shrink-0 mt-0.5" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
          
          <CardFooter className="pt-4">
            <Button 
              onClick={() => onPlanSelect(plan.id)} 
              className={`w-full ${isCurrentPlan ? 'bg-gray-200 text-gray-700 cursor-not-allowed' : 'bg-chrona-accent hover:bg-chrona-accent-light text-white'}`}
              disabled={isCurrentPlan}
            >
              {isCurrentPlan ? 'Aktueller Plan' : 'Auswählen'}
            </Button>
          </CardFooter>
        </Card>
      </motion.div>
    );
  };
  
  return (
    <section className="py-20 bg-chrona-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-chrona-text relative inline-block">
            <span className="relative z-10">Unsere Tarife</span>
            <span className="absolute -bottom-1 left-0 w-full h-1 bg-chrona-accent opacity-70"></span>
          </h2>
          <p className="mt-3 text-chrona-gray-300 max-w-2xl mx-auto">
            Finde den passenden Tarif für deine Bedürfnisse
          </p>
        </div>
        
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mx-auto mb-8">
            <TabsTrigger value="customers">Für Kunden</TabsTrigger>
            <TabsTrigger value="studios">Für Studios</TabsTrigger>
          </TabsList>

          <TabsContent value="customers" className="space-y-8">
            {showLoginButton && (
              <div className="text-center mb-8">
                <Button asChild variant="outline">
                  <a href="/auth?redirect=/pricing">Anmelden für Zugriff</a>
                </Button>
              </div>
            )}
            
            <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
              {customerPlans.map(plan => renderPlanCard(plan))}
            </div>
          </TabsContent>

          <TabsContent value="studios" className="space-y-8">
            {showLoginButton && (
              <div className="text-center mb-8">
                <Button asChild variant="outline">
                  <a href="/auth?redirect=/pricing">Anmelden für Zugriff</a>
                </Button>
              </div>
            )}
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {studioPlans.map(plan => renderPlanCard(plan))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
